import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { InternalSalesReferenceContentSchema } from '@neural-intelligence/shared';
import { useAuth } from '../AuthContext';
import { getSalesReferenceAccess, getSalesReferenceContent, updateSalesReferenceContent } from '../api';
import AppHeader from '../components/AppHeader';
import HeaderUtilityActions from '../components/HeaderUtilityActions';
import { useToast } from '../contexts/ToastContext';
import {
  internalSalesReferenceContent,
  type InternalSalesReferenceContent,
  type SalesReferenceDetailCard,
  type SalesReferenceDetailSection,
  type SalesReferenceQuickAnswer,
} from '../content/internalSalesReference';

function matchesSearch(value: string, query: string) {
  return value.toLowerCase().includes(query);
}

function quickAnswerMatches(answer: SalesReferenceQuickAnswer, query: string) {
  if (!query) {
    return true;
  }

  return [answer.question, answer.shortAnswer, answer.ifTheyPush, ...answer.tags].some((value) =>
    matchesSearch(value, query)
  );
}

function detailCardMatches(card: SalesReferenceDetailCard, query: string) {
  if (!query) {
    return true;
  }

  return [card.title, card.summary, ...card.bullets].some((value) => matchesSearch(value, query));
}

function filterDetailSections(sections: SalesReferenceDetailSection[], query: string) {
  if (!query) {
    return sections;
  }

  return sections
    .map((section) => {
      const titleMatches = [section.title, section.summary, section.whenToUse].some((value) =>
        matchesSearch(value, query)
      );
      const cards = titleMatches ? section.cards : section.cards.filter((card) => detailCardMatches(card, query));
      return {
        ...section,
        cards,
      };
    })
    .filter((section) => section.cards.length > 0);
}

function buildExpandedQuickAnswerState(content: InternalSalesReferenceContent) {
  return Object.fromEntries(content.quickAnswers.map((answer, index) => [answer.id, index < 3]));
}

function buildExpandedDetailSectionState(content: InternalSalesReferenceContent) {
  return Object.fromEntries(content.detailSections.map((section, index) => [section.id, index < 2]));
}

export default function InternalSalesReference() {
  const { user } = useAuth();
  const { toast } = useToast();
  const sectionNavRef = useRef<HTMLDivElement | null>(null);
  const isAdmin = user?.role === 'admin';
  const [query, setQuery] = useState('');
  const [content, setContent] = useState<InternalSalesReferenceContent>(internalSalesReferenceContent);
  const [salesReferenceEnabled, setSalesReferenceEnabled] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentLoadError, setContentLoadError] = useState<string | null>(null);
  const [contentReloadToken, setContentReloadToken] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [sectionNavHeight, setSectionNavHeight] = useState(0);
  const [expandedQuickAnswers, setExpandedQuickAnswers] = useState<Record<string, boolean>>(() =>
    buildExpandedQuickAnswerState(internalSalesReferenceContent)
  );
  const [expandedDetailSections, setExpandedDetailSections] = useState<Record<string, boolean>>(() =>
    buildExpandedDetailSectionState(internalSalesReferenceContent)
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editorValue, setEditorValue] = useState(() => JSON.stringify(internalSalesReferenceContent, null, 2));
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (user?.role === 'admin') {
      setSalesReferenceEnabled(true);
      setAccessLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (user?.role !== 'sales') {
      setSalesReferenceEnabled(false);
      setAccessLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setAccessLoading(true);
    getSalesReferenceAccess().then((result) => {
      if (!cancelled) {
        setSalesReferenceEnabled(Boolean(result.success && result.data?.sales_reference_enabled));
      }
    }).finally(() => {
      if (!cancelled) {
        setAccessLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  useEffect(() => {
    const updateOffsets = () => {
      const headerElement = document.querySelector('.header');
      setHeaderHeight(Math.ceil(headerElement?.getBoundingClientRect().height ?? 0));
      setSectionNavHeight(Math.ceil(sectionNavRef.current?.getBoundingClientRect().height ?? 0));
    };

    updateOffsets();

    window.addEventListener('resize', updateOffsets);

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOffsets) : null;
    const headerElement = document.querySelector('.header');

    if (resizeObserver && headerElement) {
      resizeObserver.observe(headerElement);
    }

    if (resizeObserver && sectionNavRef.current) {
      resizeObserver.observe(sectionNavRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateOffsets);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (accessLoading) {
      return () => {
        cancelled = true;
      };
    }

    const canLoadContent = isAdmin || (user?.role === 'sales' && salesReferenceEnabled);
    if (!canLoadContent) {
      setContentLoadError(null);
      setContentLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setContentLoading(true);
    setContentLoadError(null);
    getSalesReferenceContent().then((result) => {
      if (cancelled) {
        return;
      }

      if (result.success) {
        const nextContent = result.data?.content ?? internalSalesReferenceContent;
        setContentLoadError(null);
        setContent(nextContent);
        setEditorValue(JSON.stringify(nextContent, null, 2));
        return;
      }

      setContentLoadError(result.error || 'Failed to load sales reference content');
      setIsEditing(false);

      if (isAdmin) {
        toast.error(result.error || 'Failed to load sales reference content');
      }
    }).finally(() => {
      if (!cancelled) {
        setContentLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [accessLoading, contentReloadToken, isAdmin, salesReferenceEnabled, toast, user?.role]);

  useEffect(() => {
    setExpandedQuickAnswers(buildExpandedQuickAnswerState(content));
    setExpandedDetailSections(buildExpandedDetailSectionState(content));
  }, [content]);

  const normalizedQuery = query.trim().toLowerCase();
  const quickAnswers = useMemo(
    () => content.quickAnswers.filter((answer) => quickAnswerMatches(answer, normalizedQuery)),
    [content, normalizedQuery]
  );
  const detailSections = useMemo(
    () => filterDetailSections(content.detailSections, normalizedQuery),
    [content, normalizedQuery]
  );
  const searchIsActive = normalizedQuery.length > 0;
  const noResults = quickAnswers.length === 0 && detailSections.length === 0;

  const stickyNavigationTop = (headerHeight || 72) + 16;
  const anchorScrollMargin = stickyNavigationTop + (sectionNavHeight || 64) + 16;

  if (user?.role === 'customer') {
    return <Navigate to="/" replace />;
  }

  if (user?.role === 'sales' && accessLoading) {
    return (
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (user?.role === 'sales' && !salesReferenceEnabled) {
    return <Navigate to="/" replace />;
  }

  if ((isAdmin || (user?.role === 'sales' && salesReferenceEnabled)) && contentLoading) {
    return (
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  const sectionStyle: React.CSSProperties = {
    background: 'var(--color-bg-white)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    boxShadow: 'var(--shadow-sm)',
  };

  const secondaryCardStyle: React.CSSProperties = {
    background: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '18px',
  };

  const anchorStyle: React.CSSProperties = {
    scrollMarginTop: `${anchorScrollMargin}px`,
  };

  async function saveContent() {
    let parsedJson: unknown;

    try {
      parsedJson = JSON.parse(editorValue);
    } catch {
      toast.error('Sales reference JSON is invalid');
      return;
    }

    const parsedContent = InternalSalesReferenceContentSchema.safeParse(parsedJson);
    if (!parsedContent.success) {
      toast.error(parsedContent.error.issues[0]?.message || 'Sales reference JSON is invalid');
      return;
    }

    setSavingContent(true);
    const result = await updateSalesReferenceContent(parsedContent.data);

    if (result.success) {
      const nextContent = result.data?.content ?? parsedContent.data;
      setContent(nextContent);
      setEditorValue(JSON.stringify(nextContent, null, 2));
      setIsEditing(false);
      toast.success('Sales reference saved');
    } else {
      toast.error(result.error || 'Failed to save sales reference');
    }

    setSavingContent(false);
  }

  function startEdit() {
    setEditorValue(JSON.stringify(content, null, 2));
    setIsEditing(true);
  }

  function cancelEdit() {
    setEditorValue(JSON.stringify(content, null, 2));
    setIsEditing(false);
  }

  function toggleQuickAnswer(id: string) {
    setExpandedQuickAnswers((current) => ({
      ...current,
      [id]: !current[id],
    }));
  }

  function toggleDetailSection(id: string) {
    setExpandedDetailSections((current) => ({
      ...current,
      [id]: !current[id],
    }));
  }

  const headerActions = (
    <div className="flex flex-center gap-2">
      {isAdmin && !isEditing && !contentLoadError && (
        <button type="button" className="btn btn-secondary btn-small" onClick={startEdit}>
          Edit JSON
        </button>
      )}
      {isAdmin && isEditing && (
        <>
          <button type="button" className="btn btn-primary btn-small" onClick={() => void saveContent()} disabled={savingContent}>
            {savingContent ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={cancelEdit} disabled={savingContent}>
            Cancel
          </button>
        </>
      )}
      <HeaderUtilityActions current="sales-reference" />
    </div>
  );

  return (
    <div>
      <AppHeader actions={headerActions}>
        <div className="flex flex-center gap-3">
          <Link to="/" className="nav-link nav-link-brand">
            <img src="/logo.png" alt="Neural Intelligence" className="nav-logo" />
            Dashboard
          </Link>
          <span className="breadcrumb-separator">/</span>
          <h1>Internal Sales Reference</h1>
        </div>
      </AppHeader>

      <div className="container" style={{ paddingBottom: '40px' }}>
        {contentLoadError && (
          <div style={{ ...sectionStyle, marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ maxWidth: '720px' }}>
                <h2 style={{ margin: '0 0 6px', fontSize: '24px', color: 'var(--color-text-primary)' }}>Sales reference unavailable</h2>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  {contentLoadError}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setContentReloadToken((current) => current + 1)}
              >
                Retry load
              </button>
            </div>
          </div>
        )}

        <div style={{ display: contentLoadError ? 'none' : undefined }}>
        {isAdmin && isEditing && (
          <div style={{ ...sectionStyle, marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ maxWidth: '720px' }}>
                <h2 style={{ margin: '0 0 6px', fontSize: '24px', color: 'var(--color-text-primary)' }}>Edit Sales Reference JSON</h2>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  Admins can edit any copy here. Add sections or reorder content by editing the JSON arrays directly.
                </p>
              </div>
              <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '13px', maxWidth: '300px' }}>
                The saved content remains visible below until you save this JSON.
              </p>
            </div>
            <textarea
              className="input"
              value={editorValue}
              onChange={(event) => setEditorValue(event.target.value)}
              spellCheck={false}
              style={{ width: '100%', minHeight: '420px', fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace', fontSize: '13px', lineHeight: 1.6, resize: 'vertical' }}
            />
          </div>
        )}

        <div style={{ ...sectionStyle, marginTop: '24px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
              gap: '20px',
            }}
          >
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  borderRadius: '999px',
                  background: 'var(--color-primary-light)',
                  color: 'var(--color-primary)',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  marginBottom: '12px',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
                Use This First
              </div>
              <h2 style={{ fontSize: '28px', lineHeight: 1.2, margin: '0 0 12px', color: 'var(--color-text-primary)' }}>
                The simple trust story sales should lead with
              </h2>
              <p style={{ fontSize: '16px', lineHeight: 1.7, color: 'var(--color-text-primary)', margin: 0 }}>
                {content.defaultPitch}
              </p>
            </div>

            <div style={{ ...secondaryCardStyle, alignSelf: 'start' }}>
              <h3 style={{ fontSize: '16px', margin: '0 0 12px', color: 'var(--color-text-primary)' }}>How to use this page</h3>
              <ol style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                {content.useOrder.map((step, index) => (
                  <li key={`${index}-${step}`} style={{ marginBottom: '10px' }}>{step}</li>
                ))}
              </ol>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px',
              marginTop: '20px',
            }}
          >
            {content.strongestTalkingPoints.map((point, index) => (
              <div key={`${index}-${point}`} style={secondaryCardStyle}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'var(--color-success-light)',
                      color: 'var(--color-success)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p style={{ margin: 0, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>{point}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            ...sectionStyle,
            marginTop: '20px',
            background: 'var(--color-warning-light)',
            borderColor: 'var(--color-warning)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-warning)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--color-text-primary)' }}>Escalate instead of improvising</h3>
              <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
                If the conversation crosses into legal, compliance, vendor review, or custom commitments, stop selling from memory.
              </p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
            {content.escalationPreview.map((item, index) => (
              <div key={`${index}-${item}`} style={{ ...secondaryCardStyle, background: 'rgba(255, 255, 255, 0.55)' }}>
                <p style={{ margin: 0, color: 'var(--color-text-primary)', lineHeight: 1.5 }}>{item}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...sectionStyle, marginTop: '20px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: '12px',
              alignItems: 'end',
            }}
          >
            <div>
              <label
                htmlFor="sales-reference-search"
                style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: 'var(--color-text-primary)' }}
              >
                Find the right answer fast
              </label>
              <input
                id="sales-reference-search"
                className="input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by topic, objection, or phrase"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '14px', whiteSpace: 'nowrap' }}>
              {searchIsActive
                ? `${quickAnswers.length} quick answers and ${detailSections.length} deep-dive sections match`
                : 'Use search when a prospect asks a detailed trust question'}
            </div>
          </div>
        </div>

        <div
          ref={sectionNavRef}
          style={{
            ...sectionStyle,
            marginTop: '20px',
            position: 'sticky',
            top: `${stickyNavigationTop}px`,
            zIndex: 2,
            padding: '16px 20px',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              { id: 'quick-answers', label: 'Quick Answers' },
              ...detailSections.map((section) => ({ id: section.id, label: section.title })),
              { id: 'guardrails', label: 'Guardrails' },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="btn btn-secondary"
                style={{ textDecoration: 'none' }}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        <section id="quick-answers" style={{ ...anchorStyle, marginTop: '20px' }}>
          <div style={sectionStyle}>
            <div style={{ marginBottom: '16px' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: '24px', color: 'var(--color-text-primary)' }}>Quick Answers</h2>
              <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                This is the live-call layer. Use the short answer first. If the prospect keeps pushing, use the follow-up.
              </p>
            </div>

            {quickAnswers.length === 0 ? (
              <div style={secondaryCardStyle}>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                  No quick answers match that search. Clear the search to see the full reference.
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {quickAnswers.map((answer) => {
                  const expanded = searchIsActive || expandedQuickAnswers[answer.id];
                  return (
                    <div key={answer.id} style={secondaryCardStyle}>
                      <button
                        type="button"
                        onClick={() => toggleQuickAnswer(answer.id)}
                        style={{
                          width: '100%',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '16px',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                            First response
                          </div>
                          <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--color-text-primary)' }}>{answer.question}</h3>
                        </div>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--color-text-secondary)"
                          strokeWidth="2"
                          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      <div style={{ marginTop: expanded ? '14px' : '0', display: expanded ? 'grid' : 'none', gap: '14px' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                            Short answer
                          </div>
                          <p style={{ margin: 0, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{answer.shortAnswer}</p>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                            If they push
                          </div>
                          <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{answer.ifTheyPush}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {detailSections.map((section) => {
          const expanded = searchIsActive || expandedDetailSections[section.id];
          return (
            <section key={section.id} id={section.id} style={{ ...anchorStyle, marginTop: '20px' }}>
              <div style={sectionStyle}>
                <button
                  type="button"
                  onClick={() => toggleDetailSection(section.id)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '16px',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                      Use when
                    </div>
                    <h2 style={{ margin: '0 0 6px', fontSize: '24px', color: 'var(--color-text-primary)' }}>{section.title}</h2>
                    <p style={{ margin: '0 0 6px', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{section.summary}</p>
                    <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{section.whenToUse}</p>
                  </div>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--color-text-secondary)"
                    strokeWidth="2"
                    style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', marginTop: '10px' }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                <div
                  style={{
                    display: expanded ? 'grid' : 'none',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '14px',
                    marginTop: expanded ? '18px' : '0',
                  }}
                >
                  {section.cards.map((card, cardIndex) => (
                    <div key={`${section.id}-${cardIndex}-${card.title}`} style={secondaryCardStyle}>
                      <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: 'var(--color-text-primary)' }}>{card.title}</h3>
                      <p style={{ margin: '0 0 12px', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{card.summary}</p>
                      <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
                        {card.bullets.map((bullet, bulletIndex) => (
                          <li key={`${section.id}-${cardIndex}-${bulletIndex}`} style={{ marginBottom: '8px' }}>{bullet}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        <section id="guardrails" style={{ ...anchorStyle, marginTop: '20px' }}>
          <div style={sectionStyle}>
            <div style={{ marginBottom: '16px' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: '24px', color: 'var(--color-text-primary)' }}>Guardrails</h2>
              <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                This is the stop-sign section. Use it when a conversation starts to drift into unsupported promises.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              <div style={{ ...secondaryCardStyle, background: 'var(--color-danger-light)', borderColor: 'var(--color-danger)' }}>
                <h3 style={{ margin: '0 0 12px', color: 'var(--color-text-primary)' }}>Claims to avoid</h3>
                <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
                  {content.claimsToAvoid.map((item, index) => (
                    <li key={`${index}-${item}`} style={{ marginBottom: '8px' }}>{item}</li>
                  ))}
                </ul>
              </div>

              <div style={{ ...secondaryCardStyle, background: 'var(--color-warning-light)', borderColor: 'var(--color-warning)' }}>
                <h3 style={{ margin: '0 0 12px', color: 'var(--color-text-primary)' }}>When to escalate</h3>
                <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
                  {content.whenToEscalate.map((item, index) => (
                    <li key={`${index}-${item}`} style={{ marginBottom: '8px' }}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div style={{ ...sectionStyle, marginTop: '20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
            Safe closing answer
          </div>
          <p style={{ margin: 0, color: 'var(--color-text-primary)', lineHeight: 1.8, fontSize: '16px' }}>
            {content.safeClose}
          </p>
        </div>

        {noResults && (
          <div style={{ ...sectionStyle, marginTop: '20px' }}>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text-primary)' }}>No search results</h3>
            <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              Try a broader term like email, retention, OpenAI, visibility, widget, or compliance.
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}